import React, { useEffect, useState } from "react";
import Card from "components/card";
import {
  MdAssessment,
  MdCheckCircle,
  MdPeople,
  MdWarning,
  MdSearch,
  MdDownload,
} from "react-icons/md";
import { tokenManager } from "utils/tokenManager";

const API_ENDPOINT = "https://usibras-web-backend.onrender.com/api/shift-sms/workers-hours";

const getHeaders = () => {
  const auth = tokenManager.getAuthHeader();
  return { "Content-Type": "application/json", ...(auth ? { Authorization: auth } : {}) };
};

// ISO week helpers (mirror the backend's _monday_of_iso_week / _last_iso_week_of_year).
// All arithmetic is done in UTC so results never depend on the browser's timezone.
const getISOWeekOfUTC = (year, monthIndex, day) => {
  const d = new Date(Date.UTC(year, monthIndex, day));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
};

const getISOWeek = (date) => getISOWeekOfUTC(date.getFullYear(), date.getMonth(), date.getDate());

const getLastISOWeekOfYear = (year) => getISOWeekOfUTC(year, 11, 28);

const WorkerHoursReport = () => {
  const currentYear = new Date().getFullYear();
  const [workers, setWorkers] = useState([]);
  const [summary, setSummary] = useState({
    total_workers: 0,
    total_week_rows: 0,
    workers_with_hours: 0,
    workers_above_target: 0,
    total_hours_reported: 0,
    required_hours_per_week: 40,
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedWeek, setSelectedWeek] = useState(
    Math.min(getISOWeek(new Date()), getLastISOWeekOfYear(currentYear))
  );
  const [reportMeta, setReportMeta] = useState({
    year: null,
    week_number: null,
    week_start: "",
    week_end: "",
  });
  const [filters, setFilters] = useState({
    department: "",
    role_type: "",
    staff_id: "",
  });
  const [searchTerm, setSearchTerm] = useState("");

  const lastISOWeek = getLastISOWeekOfYear(selectedYear);

  const fetchWorkerHours = async (nextFilters = filters, nextYear = selectedYear, nextWeek = selectedWeek) => {
    setLoading(true);
    setMessage("");

    try {
      const params = new URLSearchParams();
      if (nextYear) params.append("year", String(nextYear));
      if (nextWeek) params.append("week_number", String(nextWeek));
      if (nextFilters.department) params.append("department", nextFilters.department);
      if (nextFilters.role_type) params.append("role_type", nextFilters.role_type);
      if (nextFilters.staff_id) params.append("staff_id", nextFilters.staff_id);

      const url = params.toString() ? `${API_ENDPOINT}?${params.toString()}` : API_ENDPOINT;
      const res = await fetch(url, { headers: getHeaders() });
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data?.error || data?.message || "Failed to load weekly worker hours");
      }

      setWorkers(Array.isArray(data?.data) ? data.data : []);
      setReportMeta({
        year: data?.year ?? nextYear,
        week_number: data?.week_number ?? null,
        week_start: data?.week_start ?? "",
        week_end: data?.week_end ?? "",
      });
      setSummary(data?.summary || {
        total_workers: 0,
        total_week_rows: 0,
        workers_with_hours: 0,
        workers_above_target: 0,
        total_hours_reported: 0,
        required_hours_per_week: 40,
      });
    } catch (err) {
      setWorkers([]);
      setReportMeta({ year: nextYear, week_number: null, week_start: "", week_end: "" });
      setSummary({
        total_workers: 0,
        total_week_rows: 0,
        workers_with_hours: 0,
        workers_above_target: 0,
        total_hours_reported: 0,
        required_hours_per_week: 40,
      });
      setMessage(err.message || "Could not load weekly worker hours");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWorkerHours();
  }, []);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    const nextFilters = { ...filters, [name]: value };
    setFilters(nextFilters);
    fetchWorkerHours(nextFilters, selectedYear, selectedWeek);
  };

  const handleYearChange = (value) => {
    const nextYear = Number(value) || currentYear;
    setSelectedYear(nextYear);
    fetchWorkerHours(filters, nextYear, selectedWeek);
  };

  const handleWeekChange = (value) => {
    const nextWeek = value ? Number(value) : "";
    setSelectedWeek(nextWeek);
    fetchWorkerHours(filters, selectedYear, nextWeek);
  };

  const filteredWorkers = workers.filter((worker) => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return true;

    return [worker.full_name, worker.staff_id, worker.department, worker.role_type, worker.week_number]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(query));
  });

  const totalWeekRows = summary.total_week_rows || filteredWorkers.length;
  const aboveTargetCount = Number(summary.workers_above_target ?? filteredWorkers.filter((worker) => Number(worker.hours_worked ?? 0) > Number(worker.required_hours ?? 0)).length);
  const belowTargetCount = Math.max(totalWeekRows - aboveTargetCount, 0);

  const exportCsv = () => {
    const rows = [
      [
        "Staff ID",
        "Full Name",
        "Department",
        "Role Type",
        "Year",
        "Week Number",
        "Week Start",
        "Week End",
        "Hours Worked",
        "Required Hours",
        "Remaining Hours",
        "Status",
      ],
      ...filteredWorkers.map((worker) => [
        worker.staff_id || "",
        worker.full_name || "",
        worker.department || "",
        worker.role_type || "",
        worker.year ?? selectedYear,
        worker.week_number ?? "",
        worker.week_start ?? "",
        worker.week_end ?? "",
        Number(worker.hours_worked ?? 0).toFixed(2),
        Number(worker.required_hours ?? 0).toFixed(2),
        Number(worker.remaining_hours ?? 0).toFixed(2),
        Boolean(worker.is_compliant) ? "Compliant" : "Below target",
      ]),
    ];

    const csvContent = rows
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "weekly-worker-hours-report.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="mt-5 h-full space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-3">
          <MdAssessment className="h-8 w-8 text-brand-500" />
          <div>
            <h2 className="text-2xl font-bold text-navy-700 dark:text-white">Weekly Worker Hours Report</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {reportMeta.week_start && reportMeta.week_end
                ? `Week ${reportMeta.week_number} · ${reportMeta.week_start} → ${reportMeta.week_end} · 40-hour weekly requirement`
                : "Weekly compliance is based on the 40-hour requirement."}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={exportCsv}
            disabled={filteredWorkers.length === 0}
            className="flex items-center gap-2 rounded-lg border border-brand-500 bg-white px-4 py-2 text-sm font-semibold text-brand-500 transition-colors hover:bg-brand-50 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-navy-800"
          >
            <MdDownload className="h-4 w-4" />
            Export CSV
          </button>
          <button
            type="button"
            onClick={() => fetchWorkerHours()}
            className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-600"
          >
            Reload
          </button>
        </div>
      </div>

      {message && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-900/10 dark:text-rose-300">
          {message}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
        <Card extra="p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Workers</p>
              <p className="mt-2 text-3xl font-bold text-navy-700 dark:text-white">{summary.total_workers}</p>
              <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                {summary.workers_with_hours ?? 0} with hours
              </p>
            </div>
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/30">
              <MdPeople className="h-6 w-6 text-blue-500" />
            </div>
          </div>
        </Card>

        <Card extra="p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Hours reported</p>
              <p className="mt-2 text-3xl font-bold text-navy-700 dark:text-white">
                {Number(summary.total_hours_reported ?? 0).toLocaleString(undefined, { maximumFractionDigits: 1 })}
                <span className="text-base font-semibold text-gray-400">h</span>
              </p>
              <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                {reportMeta.week_number ? `Week ${reportMeta.week_number} total` : `${selectedYear} total`}
              </p>
            </div>
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-violet-100 dark:bg-violet-900/30">
              <MdAssessment className="h-6 w-6 text-violet-500" />
            </div>
          </div>
        </Card>

        <Card extra="p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Above target</p>
              <p className="mt-2 text-3xl font-bold text-green-500">{aboveTargetCount}</p>
            </div>
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
              <MdCheckCircle className="h-6 w-6 text-green-500" />
            </div>
          </div>
        </Card>

        <Card extra="p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Below target</p>
              <p className="mt-2 text-3xl font-bold text-red-500">{belowTargetCount}</p>
            </div>
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
              <MdWarning className="h-6 w-6 text-red-500" />
            </div>
          </div>
        </Card>
      </div>

      <Card extra="p-5">
        <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-1 flex-col gap-3 sm:flex-row">
            <div className="relative flex-1">
              <MdSearch className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by worker, staff ID or department"
                className="w-full rounded-lg border border-gray-300 bg-white py-2.5 pl-9 pr-3 text-sm text-gray-700 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-navy-600 dark:bg-navy-800 dark:text-white"
              />
            </div>
            <input
              type="number"
              min="2020"
              max="2100"
              value={selectedYear}
              onChange={(e) => handleYearChange(e.target.value)}
              placeholder="Year"
              className="w-28 rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-700 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-navy-600 dark:bg-navy-800 dark:text-white"
            />
            <select
              value={selectedWeek}
              onChange={(e) => handleWeekChange(e.target.value)}
              title="ISO week"
              className="w-32 rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-700 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-navy-600 dark:bg-navy-800 dark:text-white"
            >
              <option value="">All weeks</option>
              {Array.from({ length: lastISOWeek }, (_, i) => i + 1).map((wk) => (
                <option key={wk} value={wk}>
                  Week {wk}
                </option>
              ))}
            </select>
            <input
              type="text"
              name="department"
              value={filters.department}
              onChange={handleFilterChange}
              placeholder="Department"
              className="rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-700 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-navy-600 dark:bg-navy-800 dark:text-white"
            />
            <input
              type="text"
              name="role_type"
              value={filters.role_type}
              onChange={handleFilterChange}
              placeholder="Role type"
              className="rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-700 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-navy-600 dark:bg-navy-800 dark:text-white"
            />
            <input
              type="text"
              name="staff_id"
              value={filters.staff_id}
              onChange={handleFilterChange}
              placeholder="Staff ID"
              className="rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-700 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-navy-600 dark:bg-navy-800 dark:text-white"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100 dark:divide-navy-700">
            <thead className="bg-gray-50 dark:bg-navy-800">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Staff</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Department</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Role</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Week</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Hours</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Progress</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white dark:divide-navy-700 dark:bg-navy-700">
              {loading ? (
                <tr>
                  <td colSpan={7} className="py-10 text-center text-sm text-gray-400">
                    Loading weekly worker hours…
                  </td>
                </tr>
              ) : filteredWorkers.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-10 text-center text-sm text-gray-400">
                    No worker hours data found.
                  </td>
                </tr>
              ) : (
                filteredWorkers.map((worker) => {
                  const compliant = Boolean(worker.is_compliant);
                  const target = Number(worker.required_hours ?? summary.required_hours_per_week ?? 40) || 40;
                  const hoursWorked = Number(worker.hours_worked ?? 0);
                  const progress = Math.min((hoursWorked / target) * 100, 100);
                  const remaining = Math.max(target - hoursWorked, 0);
                  const weekLabel = worker.week_number ? `Week ${worker.week_number}` : "-";

                  return (
                    <tr key={`${worker.staff_id}-${worker.year ?? selectedYear}-${worker.week_number ?? "n/a"}`} className="align-middle hover:bg-gray-50 dark:hover:bg-navy-600">
                      <td className="px-4 py-4">
                        <div className="flex flex-col">
                          <span className="text-sm font-semibold text-navy-700 dark:text-white">{worker.full_name}</span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">{worker.staff_id}</span>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-700 dark:text-gray-200">{worker.department}</td>
                      <td className="px-4 py-4 text-sm text-gray-700 dark:text-gray-200">{worker.role_type}</td>
                      <td className="px-4 py-4">
                        <div className="flex flex-col">
                          <span className="text-sm font-semibold text-gray-800 dark:text-white">{weekLabel}</span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {worker.week_start && worker.week_end ? `${worker.week_start} to ${worker.week_end}` : `${worker.year ?? selectedYear}`}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex flex-col">
                          <span className="text-sm font-semibold text-gray-800 dark:text-white">
                            {hoursWorked.toFixed(1)}h / {target}h
                          </span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {remaining > 0 ? `${remaining.toFixed(1)}h remaining` : "Target reached"}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="min-w-[180px]">
                          <div className="mb-1 flex items-center justify-between text-[11px] text-gray-500 dark:text-gray-400">
                            <span>{Math.min(progress, 100).toFixed(0)}%</span>
                            <span>{compliant ? "Met target" : "Below target"}</span>
                          </div>
                          <div className="h-2.5 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-navy-800">
                            <div
                              className={`h-full rounded-full ${
                                compliant ? "bg-green-500" : "bg-red-500"
                              }`}
                              style={{ width: `${Math.min(Math.max(progress, 0), 100)}%` }}
                            />
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-sm">
                        <span
                          className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${
                            compliant
                              ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"
                              : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300"
                          }`}
                        >
                          {compliant ? (
                            <>
                              <MdCheckCircle className="h-3.5 w-3.5" />
                              Compliant
                            </>
                          ) : (
                            <>
                              <MdWarning className="h-3.5 w-3.5" />
                              Below target
                            </>
                          )}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

export default WorkerHoursReport;
